import { Plus, Trash2 } from "lucide-react";
import {
  splitBlanks,
  type Question,
  type QuestionContent,
  type QuestionType,
} from "@hey404/quiz";
import { inputClass, labelClass } from "../admin-ui";

/**
 * One editor per question type, keyed so `QuestionCard` can dispatch on
 * `question.type` without a switch of its own.
 *
 * Each editor is fully controlled: it receives the current `content` and calls
 * `onChange` with the next one. Persisting is the caller's job.
 */
type EditorProps<T extends QuestionType> = {
  content: QuestionContent[T];
  onChange: (content: QuestionContent[T]) => void;
};

const rowClass = "flex items-center gap-2";
const smallButton =
  "border-ink bg-paper flex items-center gap-1 border-chip px-2 py-1 font-mono text-xs font-bold";

function MultipleChoiceEditor({
  content,
  onChange,
}: EditorProps<"multiple_choice">) {
  const setOption = (
    index: number,
    patch: Partial<{ text: string; correct: boolean }>,
  ) =>
    onChange({
      ...content,
      options: content.options.map((option, current) =>
        current === index ? { ...option, ...patch } : option,
      ),
    });
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
        <input
          type="checkbox"
          checked={content.multiple}
          onChange={(event) =>
            onChange({ ...content, multiple: event.target.checked })
          }
        />
        Permitir varias respuestas
      </label>
      {content.options.map((option, index) => (
        <div key={index} className={rowClass}>
          <input
            type={content.multiple ? "checkbox" : "radio"}
            name={`correct-${content.options.length}`}
            checked={option.correct}
            onChange={(event) =>
              content.multiple
                ? setOption(index, { correct: event.target.checked })
                : onChange({
                    ...content,
                    options: content.options.map((item, current) => ({
                      ...item,
                      correct: current === index,
                    })),
                  })
            }
            aria-label={`Opción ${index + 1} es correcta`}
          />
          <input
            value={option.text}
            onChange={(event) => setOption(index, { text: event.target.value })}
            placeholder={`Opción ${index + 1}`}
            className={`${inputClass} mt-0`}
          />
          <button
            type="button"
            aria-label="Quitar opción"
            disabled={content.options.length <= 2}
            onClick={() =>
              onChange({
                ...content,
                options: content.options.filter((_, i) => i !== index),
              })
            }
            className="disabled:opacity-30"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className={smallButton}
        onClick={() =>
          onChange({
            ...content,
            options: [...content.options, { text: "", correct: false }],
          })
        }
      >
        <Plus size={12} /> Opción
      </button>
    </div>
  );
}

function TrueFalseEditor({ content, onChange }: EditorProps<"true_false">) {
  return (
    <div className="flex gap-2">
      {[true, false].map((value) => (
        <button
          key={String(value)}
          type="button"
          onClick={() => onChange({ correct: value })}
          className={`border-ink font-display border-chip px-4 py-2 text-sm font-bold ${
            content.correct === value ? "bg-acid" : "bg-paper"
          }`}
        >
          {value ? "Verdadero" : "Falso"}
        </button>
      ))}
    </div>
  );
}

function WrittenEditor({ content, onChange }: EditorProps<"written">) {
  return (
    <label className={labelClass}>
      Respuesta de referencia (la ve el instructor al corregir)
      <textarea
        rows={4}
        value={content.answer}
        onChange={(event) => onChange({ answer: event.target.value })}
        className={inputClass}
      />
    </label>
  );
}

function SortEditor({ content, onChange }: EditorProps<"sort">) {
  const ordered = [...content.options].sort((a, b) => a.order - b.order);
  return (
    <div className="space-y-2">
      <p className="text-ink-soft font-mono text-xs uppercase">
        El orden de aquí es el correcto. Al alumno se le muestran mezcladas.
      </p>
      {ordered.map((option, index) => (
        <div key={option.id} className={rowClass}>
          <span className="border-ink bg-lemon font-display border-chip px-2 py-1 text-xs font-bold">
            {index + 1}
          </span>
          <input
            value={option.text}
            onChange={(event) =>
              onChange({
                options: content.options.map((item) =>
                  item.id === option.id
                    ? { ...item, text: event.target.value }
                    : item,
                ),
              })
            }
            className={`${inputClass} mt-0`}
          />
          <button
            type="button"
            aria-label="Subir"
            disabled={index === 0}
            onClick={() => {
              const previous = ordered[index - 1];
              if (!previous) return;
              onChange({
                options: content.options.map((item) =>
                  item.id === option.id
                    ? { ...item, order: previous.order }
                    : item.id === previous.id
                      ? { ...item, order: option.order }
                      : item,
                ),
              });
            }}
            className="disabled:opacity-30"
          >
            ↑
          </button>
          <button
            type="button"
            aria-label="Bajar"
            disabled={index === ordered.length - 1}
            onClick={() => {
              const next = ordered[index + 1];
              if (!next) return;
              onChange({
                options: content.options.map((item) =>
                  item.id === option.id
                    ? { ...item, order: next.order }
                    : item.id === next.id
                      ? { ...item, order: option.order }
                      : item,
                ),
              });
            }}
            className="disabled:opacity-30"
          >
            ↓
          </button>
          <button
            type="button"
            aria-label="Quitar"
            disabled={content.options.length <= 2}
            onClick={() =>
              onChange({
                options: content.options.filter(
                  (item) => item.id !== option.id,
                ),
              })
            }
            className="disabled:opacity-30"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className={smallButton}
        onClick={() =>
          onChange({
            options: [
              ...content.options,
              {
                id: crypto.randomUUID(),
                text: "",
                order: content.options.length,
              },
            ],
          })
        }
      >
        <Plus size={12} /> Elemento
      </button>
    </div>
  );
}

function MatchEditor({ content, onChange }: EditorProps<"match">) {
  return (
    <div className="space-y-2">
      <p className="text-ink-soft font-mono text-xs uppercase">
        Cada fila es un par. La columna derecha se mezcla para el alumno.
      </p>
      {content.pairs.map((pair) => (
        <div key={pair.id} className={rowClass}>
          <input
            value={pair.left}
            onChange={(event) =>
              onChange({
                pairs: content.pairs.map((item) =>
                  item.id === pair.id
                    ? { ...item, left: event.target.value }
                    : item,
                ),
              })
            }
            placeholder="Elemento"
            className={`${inputClass} mt-0`}
          />
          <span aria-hidden className="font-mono">
            →
          </span>
          <input
            value={pair.right}
            onChange={(event) =>
              onChange({
                pairs: content.pairs.map((item) =>
                  item.id === pair.id
                    ? { ...item, right: event.target.value }
                    : item,
                ),
              })
            }
            placeholder="Su par"
            className={`${inputClass} mt-0`}
          />
          <button
            type="button"
            aria-label="Quitar par"
            disabled={content.pairs.length <= 2}
            onClick={() =>
              onChange({
                pairs: content.pairs.filter((item) => item.id !== pair.id),
              })
            }
            className="disabled:opacity-30"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className={smallButton}
        onClick={() =>
          onChange({
            pairs: [
              ...content.pairs,
              { id: crypto.randomUUID(), left: "", right: "" },
            ],
          })
        }
      >
        <Plus size={12} /> Par
      </button>
    </div>
  );
}

function FillInTheBlankEditor({
  content,
  onChange,
}: EditorProps<"fill_in_the_blank">) {
  // The text drives how many blanks exist, so the editor mirrors the markers
  // found in it rather than letting the two drift apart.
  const used = splitBlanks(content.text)
    .filter((part) => part.kind === "blank")
    .map((part) => part.index);
  const missing = used.filter((index) => index >= content.blanks.length);
  return (
    <div className="space-y-3">
      <label className={labelClass}>
        Texto (usa {"{{0}}"}, {"{{1}}"}… donde va cada hueco)
        <textarea
          rows={3}
          value={content.text}
          onChange={(event) =>
            onChange({ ...content, text: event.target.value })
          }
          className={inputClass}
        />
      </label>
      {missing.length > 0 && (
        <p className="border-ink bg-bubble border-chip p-2 text-sm">
          Faltan opciones para {missing.map((i) => `{{${i}}}`).join(", ")}.
          Agrega un hueco por cada marcador.
        </p>
      )}
      {content.blanks.map((blank, blankIndex) => (
        <fieldset
          key={blankIndex}
          className="border-ink bg-paper border-chip p-3"
        >
          <legend className="font-display px-2 text-sm font-bold">
            Hueco {`{{${blankIndex}}}`}
            {!used.includes(blankIndex) && " · sin marcador en el texto"}
          </legend>
          {blank.options.map((option, optionIndex) => (
            <div key={optionIndex} className={`${rowClass} mt-2`}>
              <input
                type="radio"
                name={`blank-${blankIndex}`}
                checked={option.correct}
                onChange={() =>
                  onChange({
                    ...content,
                    blanks: content.blanks.map((item, index) =>
                      index === blankIndex
                        ? {
                            options: item.options.map((choice, current) => ({
                              ...choice,
                              correct: current === optionIndex,
                            })),
                          }
                        : item,
                    ),
                  })
                }
                aria-label={`Opción ${optionIndex + 1} es correcta`}
              />
              <input
                value={option.text}
                onChange={(event) =>
                  onChange({
                    ...content,
                    blanks: content.blanks.map((item, index) =>
                      index === blankIndex
                        ? {
                            options: item.options.map((choice, current) =>
                              current === optionIndex
                                ? { ...choice, text: event.target.value }
                                : choice,
                            ),
                          }
                        : item,
                    ),
                  })
                }
                placeholder={`Opción ${optionIndex + 1}`}
                className={`${inputClass} mt-0`}
              />
            </div>
          ))}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              className={smallButton}
              onClick={() =>
                onChange({
                  ...content,
                  blanks: content.blanks.map((item, index) =>
                    index === blankIndex
                      ? {
                          options: [
                            ...item.options,
                            { text: "", correct: false },
                          ],
                        }
                      : item,
                  ),
                })
              }
            >
              <Plus size={12} /> Opción
            </button>
            <button
              type="button"
              className={smallButton}
              disabled={content.blanks.length <= 1}
              onClick={() =>
                onChange({
                  ...content,
                  blanks: content.blanks.filter(
                    (_, index) => index !== blankIndex,
                  ),
                })
              }
            >
              <Trash2 size={12} /> Hueco
            </button>
          </div>
        </fieldset>
      ))}
      <button
        type="button"
        className={smallButton}
        onClick={() =>
          onChange({
            ...content,
            blanks: [
              ...content.blanks,
              {
                options: [
                  { text: "", correct: true },
                  { text: "", correct: false },
                ],
              },
            ],
          })
        }
      >
        <Plus size={12} /> Hueco
      </button>
    </div>
  );
}

/** Dispatches to the editor for a question's type. */
export function QuestionContentEditor({
  question,
  onChange,
}: {
  question: Question;
  onChange: (content: QuestionContent[QuestionType]) => void;
}) {
  switch (question.type) {
    case "multiple_choice":
      return (
        <MultipleChoiceEditor content={question.content} onChange={onChange} />
      );
    case "true_false":
      return <TrueFalseEditor content={question.content} onChange={onChange} />;
    case "written":
      return <WrittenEditor content={question.content} onChange={onChange} />;
    case "sort":
      return <SortEditor content={question.content} onChange={onChange} />;
    case "match":
      return <MatchEditor content={question.content} onChange={onChange} />;
    case "fill_in_the_blank":
      return (
        <FillInTheBlankEditor content={question.content} onChange={onChange} />
      );
  }
}
